import { createEqualityChecker, ManagedPromise, type NonEmptyArray, Queue, type ReadonlyNonEmptyArray, type ReadonlyRecord } from '../../../utils'

import { ConnectwareError, ConnectwareErrorType } from '../../../domain'

import type { BackendHttpMethod, BackendPath, BackendQueryParameters } from '../../Connectware'
import { type ConnectwareHTTPServiceOptions, FetchConnectwareHTTPService, type HttpRequestArgs } from '../Base'

type Request<Args, Type> = Readonly<{ args: Args, promise: ManagedPromise<Type, ConnectwareError> }>
export type NextBatch<Args, Path extends BackendPath, Method extends BackendHttpMethod<Path>, Output> = Readonly<{
    strategy: RSTBatchFetchingStrategy<Args, Path, Method>
    requests: ReadonlyNonEmptyArray<Request<Args, Output>>
}>

export type RSTBatchFetchingStrategy<Args, Path extends BackendPath, Method extends BackendHttpMethod<Path>> =
    /** From the request, only the following properties are needed */
    Pick<HttpRequestArgs<Path, Method, never>, 'path' | 'method' | 'capability'> &
        /** And the function to create parameters */
        Readonly<{ buildQueryParams: (requests: ReadonlyNonEmptyArray<Args>) => BackendQueryParameters<Path, Method> }>

const doBatchStrategiesMatch = createEqualityChecker<ReadonlyRecord<keyof RSTBatchFetchingStrategy<unknown, never, never>, unknown>>({
    /** Capability has no comparison value */
    capability: () => true,

    method: null,
    path: null,
    buildQueryParams: null,
})

/**
 * An abstract class that manages batching of API requests.
 * It groups multiple similar requests into fewer API calls to optimize network usage.
 */
export abstract class RSTBatchedConnectwareHTTPService<
    Args,
    Path extends BackendPath,
    Method extends BackendHttpMethod<Path>,
    BatchResponse,
    Output
> extends FetchConnectwareHTTPService {
    /**
     * This queue is here simply to ensure only one request at a time can be made
     * Causing them to bunch up and be done in batches
     */
    private readonly queue = new Queue()

    /**
     * Stores all requests that are waiting to be batched and sent out
     */
    private readonly pendingRequests: Request<Args, Output>[] = []

    protected static createServerNotAvailableHandler (): () => Promise<never> {
        return () => Promise.reject(new ConnectwareError(ConnectwareErrorType.SERVER_NOT_AVAILABLE, 'Resources Status Tracking is not running as expected'))
    }

    constructor (options: ConnectwareHTTPServiceOptions, protected readonly pageSize: number) {
        super(options)
    }

    /**
     * @returns a batch of requests from the queue, grouped according to batch/page size limits
     */
    private extractNextBatch (): NextBatch<Args, Path, Method, Output> | null {
        const [first, ...rest] = this.pendingRequests.splice(0, this.pendingRequests.length)

        if (!first) {
            /** Everything is empty, so no need to do anything */
            return null
        }

        const strategy: NextBatch<Args, Path, Method, Output>['strategy'] = this.resolveBatchFetchingStrategy(first.args)
        const requests: NonEmptyArray<Request<Args, Output>> = [first]

        for (const next of rest) {
            if (requests.length < this.pageSize && doBatchStrategiesMatch(strategy, this.resolveBatchFetchingStrategy(next.args))) {
                /** Add request because it both fits and the strategies align */
                requests.push(next)
            } else {
                /** Skip it for this batch and put it back in the pending requests */
                this.pendingRequests.push(next)
            }
        }

        return { strategy, requests }
    }

    /**
     * Executes the batched API call and resolves each request based on the response
     */
    private async resolveRequests (): Promise<void> {
        const batch = this.extractNextBatch()

        if (batch === null) {
            return
        }

        const request = this.createRequestArgs(batch)

        const result = await this.request(request).catch((e: ConnectwareError) => e)

        batch.requests.forEach(({ args, promise }) => {
            const match = ConnectwareError.is(result) ? result : this.resolveBatchResponse(result, args)

            if (!ConnectwareError.is(match)) {
                promise.resolve(match)
            } else {
                promise.reject(match)
            }
        })
    }

    protected abstract createRequestArgs (batch: NextBatch<Args, Path, Method, Output>): HttpRequestArgs<Path, Method, BatchResponse>

    protected abstract resolveBatchFetchingStrategy (args: Args): RSTBatchFetchingStrategy<Args, Path, Method>

    protected abstract resolveBatchResponse (response: BatchResponse, args: Args): Output | ConnectwareError

    protected fetchBatched (args: Args): Promise<Output> {
        const promise = new ManagedPromise<Output, ConnectwareError>()

        /** Add new request and queue a new fetching for it (in case it's needed) */
        this.pendingRequests.push({ args, promise })
        void this.queue.push(() => this.resolveRequests())

        return promise.promise
    }
}
